import { useRouter } from 'next/router';
import { castArray } from 'lodash';
import {
  Select, MenuItem, Checkbox, FormControl, InputLabel,
} from '@mui/material';
import { updateRouterQuery, toSlug } from 'lib/utils/filterUtils';
import { CreatorStage } from 'lib/types/teams';

interface Props {
  creatorStages: CreatorStage[];
  selectedStages?: number[];
}

export const CreatorStageFilter = ({ creatorStages, selectedStages = [] }: Props) => {
  const router = useRouter();
  // Default to all stages except hidden ones when no stages are selected
  const nonHiddenStageIds = creatorStages
    .filter((stage) => stage.trigger !== 'hidden')
    .map((stage) => stage.id);
  const values = selectedStages.length === 0 ? nonHiddenStageIds : castArray(selectedStages);

  const onChange = (stageIds: number[]) => {
    // If no stages selected, or if all non-hidden stages are selected, clear the query
    const queryValue = stageIds.length === 0
      || (stageIds.length === nonHiddenStageIds.length
        && nonHiddenStageIds.every((id) => stageIds.includes(id)))
      ? []
      : stageIds.map((id) => toSlug('', id));
    updateRouterQuery(router, { creatorStages: queryValue });
  };

  const onClose = (): void => {
    setTimeout(() => {
      (document?.activeElement as HTMLInputElement)?.blur();
    }, 0);
  };

  const renderValue = (selected: number[]) => {
    if (selected.length === 1) {
      const stage = creatorStages.find((s) => s.id === selected[0]);
      return stage?.name || 'Stage';
    }

    return `${selected.length} stages`;
  };

  return (
    <FormControl className="w-full" size="small">
      <InputLabel id="creator-stage-select-label">Stage</InputLabel>
      <Select
        className="w-full"
        label="Stage"
        labelId="creator-stage-select-label"
        id="creator-stage-select"
        multiple
        onClose={onClose}
        onChange={(e) => onChange(e.target.value as number[])}
        renderValue={renderValue}
        size="small"
        value={values || []}
      >
        {creatorStages.map((stage) => (
          <MenuItem key={stage.id} value={stage.id}>
            <div className="flex flex-1 items-center justify-between group">
              <div>
                <Checkbox
                  size="small"
                  checked={
                    selectedStages.length === 0
                      ? nonHiddenStageIds.includes(stage.id)
                      : selectedStages.includes(stage.id)
                  }
                />
                {stage.name}
              </div>
              <div className="md:hidden group-hover:block">
                <button
                  type="button"
                  className="btn-primary-xs"
                  onClick={(e) => {
                    onChange([stage.id]);
                    e.stopPropagation();
                  }}
                >
                  Only
                </button>
              </div>
            </div>
          </MenuItem>
        ))}
        <div className="text-right mt-2 pr-2">
          <button
            type="button"
            className="btn-secondary-outlined-xs"
            onClick={() => onChange([])}
            disabled={
              values.length === nonHiddenStageIds.length
              && nonHiddenStageIds.every((id) => values.includes(id))
            }
          >
            Clear
          </button>
        </div>
      </Select>
    </FormControl>
  );
};
